import { Switch as BaseSwitch } from '@base-ui/react/switch';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';

export const switchVariants = cva(
  [
    /*
     * The pointer comes from the base layer, which knows to drop it when disabled.
     * `shrink-0` because a switch lives at the end of a row whose left side is
     * text: without it, a long label squashes the track and the thumb travels
     * past its own edge.
     */
    'relative inline-flex shrink-0 items-center rounded-full bg-line px-0.75 transition-colors',
    'after:absolute after:left-1/2 after:top-1/2 after:size-11 after:-translate-x-1/2 after:-translate-y-1/2',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
    /* Root renders a <span>, so :disabled never matches — Base UI sets data-disabled */
    'data-disabled:opacity-40 data-disabled:pointer-events-none',
  ],
  {
    variants: {
      size: {
        md: 'w-10.5 h-6',
        sm: 'w-8.5 h-5',
      },
      color: {
        accent: 'data-checked:bg-accent',
        primary: 'data-checked:bg-primary',
      },
    },
    defaultVariants: {
      size: 'md',
      color: 'accent',
    },
  },
);

const switchThumbVariants = cva(
  'block rounded-full bg-white shadow-thumb transition-transform',
  {
    variants: {
      size: {
        md: 'size-4.5 data-checked:translate-x-4.5',
        sm: 'size-3.5 data-checked:translate-x-3.5',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export interface SwitchProps
  extends
    Omit<BaseSwitch.Root.Props, 'style' | 'className' | 'color'>,
    VariantProps<typeof switchVariants> {
  className?: string;
}

export function Switch({ className, size, color, ...props }: SwitchProps) {
  return (
    <BaseSwitch.Root
      data-slot='switch'
      className={cn(switchVariants({ size, color }), className)}
      {...props}
    >
      <BaseSwitch.Thumb
        data-slot='switch-thumb'
        className={switchThumbVariants({ size })}
      />
    </BaseSwitch.Root>
  );
}
